import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import {
  MoreVertical,
  Folder as FolderIcon,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Droplets,
  Trash2,
  Image as ImageIcon,
  Images,
} from 'lucide-react'
import { Folder, NewFolder } from '../../../../Data/album.dto'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import type { RootState } from '../../../../Redux/Store'
import {
  addToUpdatedFolders,
  setCurrentFolder,
} from '../../../../Redux/Slice/Dashboard/AlbumSlice'
import { useNavigate } from 'react-router-dom'
import DeletePopup from '../Folder/DeletePopup'
import HideFolderPopup from '../Folder/HidePopup'
import {
  deleteFolderAPI,
  hideFolderAPI,
  lockFolderAPI,
  toggleWatermarkAPI,
  unlockFolderAPI,
} from '../../../../Redux/ApiCalls/Dashboard/FolderApi'
import ReasonModal from '../HomePage/ReasonModal'
import { showErrorToast } from '../../Utlis/Toast'
import AllowedUserModal from '../HomePage/AllowedUserModal'
import { IMAGE_CDN } from '../../../../config/config'
import { usePermissions } from '../../../../hooks/usePermissions'

interface FolderCardProps {
  folder?: Folder
  newFolder?: NewFolder
  onClick?: () => void
  isNew: boolean
}

const MENU_WIDTH = 240
const MENU_MAX_HEIGHT = 235
const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)'

type PreviewImage = { src: string; key: string }

const FolderCard: React.FC<FolderCardProps> = ({
  folder,
  newFolder,
  onClick,
  isNew,
}) => {
  const [showReasonModal, setShowReasonModal] = useState(false)
  const [reason, setReason] = useState('')
  const [selectedReason, setSelectedReason] = useState(0)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const [isDeletePopup, setIsDeletePopup] = useState(false)
  const [isHidePopup, setIsHidePopup] = useState(false)
  const [showAllowedUserPopups, setShowAllowedUserPopups] = useState(false)
  const { hasPermission, hasAnyPermission } = usePermissions()
  const canLock = hasPermission('albums.lock')
  const canHide = hasPermission('albums.hide')
  const canWatermark = hasPermission('albums.activate_watermark')
  const canDelete = hasAnyPermission(['albums.delete', 'albums.edit_delete_media'])
  const hasAnyFolderAction = canLock || canHide || canWatermark || canDelete
  const hasPrecedingAction = canLock || canHide || canWatermark
  const uploadFolderProgress = useAppSelector(
    (state: RootState) => state.album.uploadFolderProgress
  )

  const handleClickOutside = (event: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setMenuOpen(false)
    }
  }

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen((prev) => !prev)
  }

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    let top =
      spaceBelow >= MENU_MAX_HEIGHT || spaceBelow >= spaceAbove
        ? rect.bottom + 8
        : rect.top - MENU_MAX_HEIGHT - 8

    top = Math.max(8, Math.min(top, window.innerHeight - MENU_MAX_HEIGHT - 8))

    let left = rect.right - MENU_WIDTH
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8))

    setMenuPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null)
      return
    }
    updateMenuPosition()
    window.addEventListener('scroll', updateMenuPosition, true)
    window.addEventListener('resize', updateMenuPosition)
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true)
      window.removeEventListener('resize', updateMenuPosition)
    }
  }, [menuOpen, updateMenuPosition])

  useEffect(() => {
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const handleDeleteFolder = (id: number, project_id: number) => {
    dispatch(deleteFolderAPI({ folder_id: id, project_id }))
  }

  const handleCardClick = (
    e: React.MouseEvent,
    id: number,
    project_id: number
  ) => {
    if (!menuOpen && folder) {
      if (isUploadProgress(id)) {
        showErrorToast('Please wait for upload progress.')
      } else {
        dispatch(addToUpdatedFolders({ folder_id: id, project_id }))
        dispatch(setCurrentFolder(folder))
        navigate(`/dashboard/albums/folder/${folder.id}`)
      }
    }
  }

  const handleHideFolder = (
    projectId: number,
    id: number,
    is_hide: boolean
  ) => {
    if (is_hide)
      dispatch(
        hideFolderAPI({
          project_id: projectId,
          folder_id: id,
          lock_type: 'unhide',
        })
      )
    else
      dispatch(
        hideFolderAPI({
          project_id: projectId,
          folder_id: id,
          lock_type: 'hide',
        })
      )

    setIsHidePopup(false)
  }

  const isUploadProgress = (id: number) => {
    if (
      uploadFolderProgress &&
      uploadFolderProgress.length > 0 &&
      uploadFolderProgress.find((f) => f.folderId === id)
    ) {
      return true
    }
    return false
  }

  const handleReasonSubmit = (folder_id: number, project_id: number) => {
    if (selectedReason === 4) {
      dispatch(
        lockFolderAPI({
          folder_id: folder_id,
          project_id,
          custom_reason: reason,
          lock_type: 'lock',
        })
      )
    } else {
      dispatch(
        lockFolderAPI({
          folder_id: folder_id,
          project_id,
          reason: selectedReason,
          lock_type: 'lock',
        })
      )
    }
    setShowReasonModal(false)
  }

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    setSelectedReason(parseInt(e.target.value))
  }

  const handleUnlockFolder = (folder_id: number, project_id: number) => {
    dispatch(unlockFolderAPI({ folder_id, project_id, lock_type: 'unlock' }))
  }

  const handleWatermark = (id: number, status: boolean, project_id: number) => {
    if (status)
      dispatch(
        toggleWatermarkAPI({ folder_id: id, add_watermark: 'True', project_id })
      )
    else
      dispatch(
        toggleWatermarkAPI({
          folder_id: id,
          add_watermark: 'False',
          project_id,
        })
      )
  }

  const renderPreview = (
    previews: PreviewImage[],
    totalCount: number,
    emptyLabel = 'No images yet'
  ) => {
    if (previews.length === 0) {
      return (
        <EmptyPreview>
          <EmptyIconWrap>
            <ImageIcon size={20} />
          </EmptyIconWrap>
          <EmptyText>{emptyLabel}</EmptyText>
        </EmptyPreview>
      )
    }

    const remaining = Math.max(0, totalCount - 3)

    return (
      <ThumbStack>
        {previews.slice(0, 3).map((img, index) => (
          <Thumb key={img.key} $src={img.src} $index={index} />
        ))}
        {remaining > 0 && (
          <MoreThumb $index={Math.min(previews.length, 3)}>
            +{remaining}
          </MoreThumb>
        )}
      </ThumbStack>
    )
  }

  const renderCardShell = ({
    name,
    imageCount,
    statusChips,
    menu,
    previews,
    totalCount,
    onCardClick,
    showMenuButton,
  }: {
    name: string
    imageCount: number
    statusChips?: React.ReactNode
    menu?: React.ReactNode
    previews: PreviewImage[]
    totalCount: number
    onCardClick?: (e: React.MouseEvent) => void
    showMenuButton?: boolean
  }) => (
    <Card onClick={onCardClick}>
      <CardTop>
        <TitleBlock>
          <FolderBadge>
            <FolderIcon size={14} />
          </FolderBadge>
          <TitleMeta>
            <FolderName title={name}>{name}</FolderName>
            <FolderMeta>
              <Images size={12} strokeWidth={2.2} />
              {imageCount} {imageCount === 1 ? 'image' : 'images'}
            </FolderMeta>
          </TitleMeta>
        </TitleBlock>

        {showMenuButton && (
          <MenuButton
            ref={buttonRef}
            type="button"
            onClick={openMenu}
            aria-label="Folder options"
            aria-expanded={menuOpen}
          >
            <MoreVertical size={16} />
          </MenuButton>
        )}
      </CardTop>

      {statusChips ? <StatusRow>{statusChips}</StatusRow> : null}

      <PreviewArea>{renderPreview(previews, totalCount)}</PreviewArea>
      {menu}
    </Card>
  )

  return (
    <CardRoot>
      {folder ? (
        <>
          {createPortal(
            <>
              {showAllowedUserPopups && (
                <AllowedUserModal
                  folder={folder}
                  isAlbum={false}
                  onClose={() => setShowAllowedUserPopups(false)}
                />
              )}
              {isDeletePopup && (
                <DeletePopup
                  buttonText="Delete"
                  text="Are you sure you want to delete this folder?"
                  cancel={() => setIsDeletePopup(false)}
                  Delete={() => handleDeleteFolder(folder.id, folder.project_id)}
                />
              )}
              {isHidePopup && (
                <HideFolderPopup
                  Hide={() =>
                    handleHideFolder(folder.project_id, folder.id, folder.is_hide)
                  }
                  cancel={() => setIsHidePopup(false)}
                  is_hide={!folder.is_hide}
                />
              )}
              {showReasonModal && (
                <ReasonModal
                  selectedReason={selectedReason}
                  handleSubmit={() =>
                    handleReasonSubmit(folder.id, folder.project_id)
                  }
                  setShowReasonModal={setShowReasonModal}
                  setReason={setReason}
                  handleReasonChange={handleReasonChange}
                  reason={reason}
                />
              )}
            </>,
            document.body
          )}

          {renderCardShell({
            name: folder.name,
            imageCount: folder.total_images ?? folder.images?.length ?? 0,
            showMenuButton: hasAnyFolderAction,
            onCardClick: (e) => handleCardClick(e, folder.id, folder.project_id),
            statusChips: (
              <>
                {folder.is_locked ? (
                  <StatusChip>
                    <Lock size={11} strokeWidth={2.2} />
                    Locked
                  </StatusChip>
                ) : null}
                {folder.is_hide ? (
                  <StatusChip>
                    <EyeOff size={11} strokeWidth={2.2} />
                    Hidden
                  </StatusChip>
                ) : null}
                {folder.activate_watermark ? (
                  <StatusChip>
                    <Droplets size={11} strokeWidth={2.2} />
                    Watermark
                  </StatusChip>
                ) : null}
              </>
            ),
            previews: (folder.images || []).slice(0, 3).map((img, i) => ({
              key: `${folder.id}-${i}`,
              src: `${IMAGE_CDN}/${img.thumbnail}`,
            })),
            totalCount: folder.total_images ?? folder.images?.length ?? 0,
            menu:
              hasAnyFolderAction && menuOpen && menuPosition
                ? createPortal(
                  <DropdownMenu
                    ref={menuRef}
                    $top={menuPosition.top}
                    $left={menuPosition.left}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {canLock && (
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuOpen(false)
                          if (folder.is_locked) {
                            handleUnlockFolder(folder.id, folder.project_id)
                          } else {
                            setShowReasonModal(true)
                          }
                        }}
                      >
                        <ItemIconWrap>
                          {folder.is_locked ? <Unlock size={15} /> : <Lock size={15} />}
                        </ItemIconWrap>
                        <ItemName>
                          {folder.is_locked ? 'Unlock Folder' : 'Lock Folder'}
                        </ItemName>
                      </MenuItem>
                    )}

                    {canHide && (
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuOpen(false)
                          setIsHidePopup(true)
                        }}
                      >
                        <ItemIconWrap>
                          {folder.is_hide ? <Eye size={15} /> : <EyeOff size={15} />}
                        </ItemIconWrap>
                        <ItemName>
                          {folder.is_hide ? 'Unhide Folder' : 'Hide Folder'}
                        </ItemName>
                      </MenuItem>
                    )}

                    {canWatermark && (
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuOpen(false)
                          handleWatermark(
                            folder.id,
                            !folder.activate_watermark,
                            folder.project_id
                          )
                        }}
                      >
                        <ItemIconWrap>
                          <Droplets size={15} />
                        </ItemIconWrap>
                        <ItemName>
                          {folder.activate_watermark
                            ? 'Remove Watermark'
                            : 'Activate Watermark'}
                        </ItemName>
                      </MenuItem>
                    )}

                    {canDelete && hasPrecedingAction && <MenuDivider />}

                    {canDelete && (
                      <MenuItem
                        $danger
                        onClick={(e) => {
                          e.stopPropagation()
                          if (isUploadProgress(folder.id)) {
                            showErrorToast('Please wait for image uploading')
                            return
                          }
                          setMenuOpen(false)
                          setIsDeletePopup(true)
                        }}
                      >
                        <ItemIconWrap $danger>
                          <Trash2 size={15} />
                        </ItemIconWrap>
                        <ItemName $danger>Delete Folder</ItemName>
                      </MenuItem>
                    )}
                  </DropdownMenu>,
                  document.body
                )
                : null,
          })}
        </>
      ) : null}

      {newFolder
        ? renderCardShell({
          name: newFolder.name,
          imageCount: newFolder.images?.length ?? 0,
          onCardClick: onClick,
          previews: (newFolder.images || []).slice(0, 3).map((img, i) => ({
            key: `new-${i}`,
            src: img.image_blob,
          })),
          totalCount: newFolder.images?.length ?? 0,
        })
        : null}
    </CardRoot>
  )
}

export default FolderCard

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`

const CardRoot = styled.div`
  width: 100%;
`

const Card = styled.article`
  position: relative;
  width: 100%;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.07);
  box-shadow: 0 4px 18px rgba(23, 23, 23, 0.05);
  padding: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(201, 77, 170, 0.18);
    box-shadow: 0 12px 28px rgba(23, 23, 23, 0.09);
  }
`

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const TitleBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1;
`

const FolderBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 6px 14px rgba(201, 77, 170, 0.22);
`

const TitleMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const FolderName = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 750;
  line-height: 1.25;
  color: #171717;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FolderMeta = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`

const MenuButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fafafa;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.55);
  flex-shrink: 0;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.28);
    background: rgba(201, 77, 170, 0.05);
  }
`

const StatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 999px;
  font-family: Urbanist, sans-serif;
  font-size: 11px;
  font-weight: 650;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.14);
`

const PreviewArea = styled.div`
  min-height: 72px;
  display: flex;
  align-items: center;
`

const ThumbStack = styled.div`
  position: relative;
  height: 64px;
  width: 100%;
`

const Thumb = styled.div<{ $src: string; $index: number }>`
  position: absolute;
  left: ${(p) => p.$index * 42}px;
  top: 0;
  width: 64px;
  height: 64px;
  border-radius: 50px;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
  border: 2px solid #fff;
  box-shadow: 0 6px 16px rgba(23, 23, 23, 0.12);
  z-index: ${(p) => p.$index + 1};
`

const MoreThumb = styled.div<{ $index: number }>`
  position: absolute;
  left: ${(p) => p.$index * 42}px;
  top: 0;
  width: 64px;
  height: 64px;
  border-radius: 50px;
  border: 2px solid #fff;
  background: ${gradient};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  z-index: ${(p) => p.$index + 2};
`

const EmptyPreview = styled.div`
  width: 100%;
  min-height: 64px;
  border-radius: 14px;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  background: rgba(248, 250, 252, 0.9);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
`

const EmptyIconWrap = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(201, 77, 170, 0.85);
  background: rgba(201, 77, 170, 0.08);
  flex-shrink: 0;
`

const EmptyText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
`

const DropdownMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${MENU_WIDTH}px;
  max-height: ${MENU_MAX_HEIGHT}px;
  overflow-y: auto;
  z-index: 10050;
  background: #fff;
  border-radius: 14px;
  padding: 6px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 14px 40px rgba(23, 23, 23, 0.14);
  animation: ${fadeIn} 0.15s ease-out;
`

const MenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  color: ${(p) => (p.$danger ? '#e44862' : '#171717')};
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) =>
    p.$danger ? 'rgba(228, 72, 98, 0.08)' : 'rgba(201, 77, 170, 0.06)'};
  }
`

const MenuDivider = styled.div`
  height: 1px;
  margin: 4px 8px;
  background: rgba(23, 23, 23, 0.08);
`

const ItemIconWrap = styled.div<{ $danger?: boolean }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${(p) => (p.$danger ? '#e44862' : gradient)};
`

const ItemName = styled.span<{ $danger?: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  color: ${(p) => (p.$danger ? '#e44862' : '#171717')};
`
